import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog as DialogRoot,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface DeleteModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Title of the modal
   * @default "Are you sure?"
   */
  title?: string;
  /**
   * Optional entity name to be used in the description translation
   */
  entity?: string;
  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;
  /**
   * Text for the delete button
   * @default "Delete"
   */
  deleteText?: string;
  /**
   * Text to show when deleting is in progress
   * @default "Deleting"
   */
  deletingText?: string;
  /**
   * Whether the delete operation is in progress
   */
  isPending: boolean;
  /**
   * Callback when the delete button is clicked
   */
  onDelete: () => void;
  /**
   * Callback when the cancel button is clicked
   * @default closes the modal
   */
  onCancel?: () => void;
}

/**
 * A reusable delete confirmation modal component
 */
export function DeleteModal({
  open,
  onOpenChange,
  title = "Are you sure?",
  description,
  entity,
  cancelText,
  deleteText,
  deletingText,
  isPending,
  onDelete,
  onCancel,
}: DeleteModalProps) {
  const { t } = useTranslation();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  // If description is a translation key and entity is provided, use t with options
  const translatedDescription = entity
    ? t("common.delete-description", { entity })
    : t("common.delete-description");

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{translatedDescription}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {cancelText || t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={onDelete} disabled={isPending}>
            {isPending
              ? deletingText || t("common.deleting")
              : deleteText || t("common.delete")}
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
